import type { GlobalSettings, ContentfulApiInitConfig, ContentPageEntryType } from 'tsconfig/types.d';
import { ContentfulClient } from './contentful/contentfulClient';
import { compressContentfulJson, logger } from 'common-ui';

// Define `initContentfulAPI` before `initContentfulAPIWithGlobalSettings`
export const initContentfulAPI = (config: ContentfulApiInitConfig) => {
  const contentfulClient = new ContentfulClient(config);
  const client = contentfulClient.primaryClient;

  const processResponse = (response: any) => {
    if (config.isPreview) {
      return response;
    }
    return compressContentfulJson(response);
  };

  return {
    // Fetches a list of all Content Pages with pagination
    // Note: Pages are already filtered by site via Contentful space/environment
    fetchListOfAllContentPages: async () => {
      try {
        const limit = 1000; // Max limit per request

        // Recursive function to fetch all pages with pagination
        const fetchPaginatedPages = async (skip = 0, allItems: any[] = []): Promise<any[]> => {
          // Safety check to prevent infinite loops
          if (skip >= 10000) {
            logger.error('fetchListOfAllContentPages: Too many pages, stopping pagination at 10000');
            return allItems;
          }

          const queryParams: any = {
            content_type: 'contentPage',
            include: 0, // No child items - we only want the list of pages
            limit,
            skip,
          };

          const rawPagesData = await client.withoutUnresolvableLinks.getEntries(queryParams);
          const newItems = allItems.concat(rawPagesData.items);

          // Check if there are more items to fetch
          const hasMorePages = rawPagesData.total > skip + limit;

          if (hasMorePages) {
            return fetchPaginatedPages(skip + limit, newItems);
          }

          return newItems;
        };

        const allItems = await fetchPaginatedPages();
        logger.log(
          `Fetched ${allItems.length} total pages from Contentful (across ${Math.ceil(allItems.length / limit)} requests)`,
        );
        return processResponse(allItems);
      } catch (e) {
        logger.error('Error', e);
      }
    },

    //fetches a list of all Landing Pages
    fetchListOfAllLandingPages: async () => {
      try {
        const rawPagesData = await client.withoutUnresolvableLinks.getEntries({
          content_type: 'acquisitionLandingPage',
          //no child items - we only want the list of pages
          include: 0,
        });
        return processResponse(rawPagesData.items);
      } catch (e) {
        logger.error('Error', e);
      }
    },

    fetchSiteConfig: async () => {
      const siteConfig = await contentfulClient.fetchSiteConfig();
      return processResponse(siteConfig);
    },

    fetchContentPageForSlug: async (slug: string): Promise<ContentPageEntryType> => {
      const page = await contentfulClient.fetchContentPage({ 'fields.slug': slug });
      if (page && page?.length > 1) {
        logger.error(
          `More than 1 ContentPage item found for slug '${slug}'. Using first`,
          JSON.stringify(page, null, 2),
        );
      }
      return processResponse(page?.[0]);
    },

    // fetches a single Content Page by its ID
    fetchContentPageForId: async (id: string) => {
      const page = await contentfulClient.fetchContentPage({ 'fields.id': id });
      if (page && page?.length > 1) {
        logger.error(`More than 1 ContentPage item found for id '${id}'. Using first`, JSON.stringify(page, null, 2));
      }
      return page?.[0];
    },

    // Fetch Landing Page content by slug
    fetchLandingPageForSlug: async (slug: string) => {
      const page = await contentfulClient.fetchLandingPage({ 'fields.slug': slug });
      if (page && page?.length > 1) {
        logger.error(
          `More than 1 ContentPage item found for slug '${slug}'. Using first`,
          JSON.stringify(page, null, 2),
        );
      }
      return processResponse(page?.[0]);
    },
  };
};

// Use `initContentfulAPI` after its definition
export const initContentfulAPIWithGlobalSettings = (globalSettings: GlobalSettings) =>
  initContentfulAPI({
    space: globalSettings.contentfulSpace as string,
    environment: globalSettings.contentfulEnvironment as string,
    accessToken: (globalSettings.contentfulPreviewMode
      ? globalSettings.contentfulPreviewToken
      : globalSettings.contentfulToken) as string,
    host: globalSettings.contentfulPreviewMode ? 'preview.contentful.com' : 'cdn.contentful.com',
    contentfulOrchestration: globalSettings.contentfulOrchestration as string,
    isPreview: globalSettings.contentfulPreviewMode as boolean,
  });
